import { useCallback, useEffect, useReducer, useRef } from "react";
import { searchOrderInvoiceService as defaultService } from "../services/searchOrderInvoiceService";

// ─── State Machine ────────────────────────────────────────────────────────────

const INITIAL_STATE = { status: "idle", data: null, error: null };

function reducer(state, action) {
  switch (action.type) {
    case "FETCH_START":
      return { ...state, status: "loading", error: null };
    case "REFRESH_START":
      return { ...state, status: "refreshing", error: null };
    case "FETCH_SUCCESS":
      return { status: "success", data: action.payload, error: null };
    case "FETCH_ERROR":
      return { ...state, status: "error", error: action.payload };
    default:
      return { ...state };
  }
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Low-level composable hook. Executes search_order_invoice whenever `params`
 * reference changes. Callers must memoize `params`.
 *
 * Race condition guard: requestId ref discards results from stale in-flight
 * requests that resolve after a newer one has been dispatched.
 *
 * `service` is injected for testability.
 */
export function useGetSearchOrderInvoice(params, service = defaultService) {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  const hasLoadedOnce = useRef(false);
  const requestId = useRef(0);

  const execute = useCallback(
    async forceRefresh => {
      // eslint-disable-next-line no-plusplus
      const currentId = ++requestId.current;
      dispatch({
        type:
          forceRefresh || hasLoadedOnce.current
            ? "REFRESH_START"
            : "FETCH_START",
      });
      try {
        const page = await service.searchOrderInvoices(params);
        if (currentId !== requestId.current) return;
        hasLoadedOnce.current = true;
        dispatch({ type: "FETCH_SUCCESS", payload: page });
      } catch (err) {
        if (currentId !== requestId.current) return;
        dispatch({ type: "FETCH_ERROR", payload: err });
      }
    },
    [service, params],
  );

  useEffect(() => {
    execute(false);
  }, [execute]);

  const refresh = useCallback(() => execute(true), [execute]);

  return {
    status: state.status,
    orderPage: state.data,
    error: state.error,
    refresh,
  };
}

export default {};
